import React, { useState, useRef, useEffect } from 'react';
import '../../../../../../../Assets/styles/LandingStyles/LandingComponentsStyles/BeginnersGuideStyles/SudokuBasicsStyles/SudokuBasicsTutorial.css';
import { useLanguage } from 'Core/Translations/LanguageContext';

export default function TutorialStep1({ stepNumber = 1 }: { stepNumber?: number }) {
    const { t } = useLanguage();
    const localStorageKey = `tutorial-step-${stepNumber}-answer`;
    const [selectedNumber, setSelectedNumber] = useState<number | null>(null);
    const [showFeedback, setShowFeedback] = useState(false);
    const [isCorrect, setIsCorrect] = useState(false);
    const [isEditing, setIsEditing] = useState(false);

    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (isEditing && inputRef.current) {
            inputRef.current.focus();
        }
    }, [isEditing]);

    const gridData = [
        [null, 7, 6],
        [1, 5, 3],
        [8, 2, 4]
    ];

    // Load cached answer on mount
    useEffect(() => {
        const cached = localStorage.getItem(localStorageKey);
        if (cached !== null) {
            const parsed = parseInt(cached, 10);
            if (!isNaN(parsed)) {
                setSelectedNumber(parsed);
                setIsCorrect(parsed === 9);
                setShowFeedback(true);
            }
        }
    }, [localStorageKey]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        const parsedNumber = parseInt(value, 10);

        if (value === '') {
            setSelectedNumber(null);
            return;
        }

        if (!isNaN(parsedNumber)) {
            setSelectedNumber(parsedNumber);
            if (value.length === 1) {
                finalizeInput(parsedNumber);
            }
        } else {
            finalizeInput(null);
        }
    };

    const finalizeInput = (value: number | null) => {
        if (value !== null) {
            const finalValue = Math.min(9, Math.max(1, value));
            const correct = finalValue === 9;
            setSelectedNumber(finalValue);
            setIsCorrect(correct);
            setShowFeedback(true);
            localStorage.setItem(localStorageKey, String(finalValue));
        } else {
            setSelectedNumber(null);
            setShowFeedback(false);
            localStorage.removeItem(localStorageKey);
        }
        setIsEditing(false);
    };

    const handleInputBlur = () => {
        const value = inputRef.current?.value;
        finalizeInput(value ? parseInt(value, 10) : null);
    };

    const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (!/[0-9]|Backspace|Delete|ArrowLeft|ArrowRight|Tab/.test(e.key)) {
            e.preventDefault();
        }
        if (e.key === 'Enter') {
            e.currentTarget.blur();
        }
    };

    const handleCellClick = () => {
        if (isCorrect) return;
        if (selectedNumber !== null) {
            setSelectedNumber(null);
            setShowFeedback(false);
            localStorage.removeItem(localStorageKey);
        }
        setIsEditing(true);
    };

    const handleOptionClick = (num: number) => {
        if (isCorrect) return;
        finalizeInput(num);
    };

    const targetCellClassName = selectedNumber === null
        ? "cell target-cell empty"
        : `cell target-cell selected-cell ${isCorrect ? 'correct' : 'incorrect'}`;

    const inputClassName = selectedNumber === null
        ? "cell-input big-input empty"
        : `cell-input big-input selected-cell ${isCorrect ? 'correct' : 'incorrect'}`;

    return (
        <div className="tutorial-step-container">
            <div className="content-container">
                <div className="grid-wrapper">
                    <div className="sudoku-grid">
                        {gridData.map((row, rowIndex) =>
                            row.map((num, colIndex) => {
                                if (rowIndex === 0 && colIndex === 0) {
                                    return (
                                        <div
                                            key={`${rowIndex}-${colIndex}`}
                                            className={targetCellClassName}
                                            onClick={handleCellClick}
                                        >
                                            {isEditing ? (
                                                <input
                                                    type="number"
                                                    ref={inputRef}
                                                    value={selectedNumber !== null ? selectedNumber : ''}
                                                    onChange={handleInputChange}
                                                    onBlur={handleInputBlur}
                                                    onKeyDown={handleInputKeyDown}
                                                    className={inputClassName}
                                                    min="1"
                                                    max="9"
                                                    inputMode="numeric"
                                                    pattern="[1-9]*"
                                                    style={{ width: '100%', height: '100%' }}
                                                />
                                            ) : (
                                                selectedNumber !== null ? selectedNumber : ''
                                            )}
                                        </div>
                                    );
                                }
                                return (
                                    <div
                                        key={`${rowIndex}-${colIndex}`}
                                        className={`cell ${num === null ? 'empty' : ''}`}
                                    >
                                        {num !== null ? num : ''}
                                    </div>
                                );
                            })
                        )}
                    </div>
                    <span className="grid-size-label">3 X 3</span>
                </div>
                <div className="text-content">
                    <div className="header-container-tutorial">
                        <h2 className="header-tutorial">{t.beginnersGuide.basics.fillMissing}</h2>
                        {showFeedback && (
                            <span className={`status-text ${isCorrect ? 'correct' : 'incorrect'}`}>
                                {isCorrect ? t.beginnersGuide.basics.correct : t.beginnersGuide.basics.incorrect}
                            </span>
                        )}
                    </div>
                    <p className="instructions">
                        {t.beginnersGuide.basics.instructions}
                    </p>
                    <div className="options-row">
                        {[3, 5, 7, 9].map((num, index) => {
                            let indicatorClass = "option-indicator";
                            let buttonClass = "option-button";

                            if (showFeedback && selectedNumber === num) {
                                if (num === 9) {
                                    indicatorClass += " correct";
                                    buttonClass += " correct";
                                } else {
                                    indicatorClass += " incorrect";
                                    buttonClass += " selected-wrong";
                                }
                            }

                            return (
                                <div className="option-container" key={num}>
                                    <button
                                        className={buttonClass}
                                        onClick={() => handleOptionClick(num)}
                                        disabled={isCorrect}
                                    >
                                        {num}
                                    </button>
                                    <div className={indicatorClass} />
                                    <span className="option-label">
                                        {String.fromCharCode(65 + index)}
                                    </span>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
            {showFeedback && (
                <div className={`feedback-message ${isCorrect ? 'success' : 'error'}`}>
                    {isCorrect ? t.beginnersGuide.basics.feedbackCorrect : t.beginnersGuide.basics.feedbackIncorrect}
                </div>
            )}
        </div>
    );
}